import { ApiState } from '../../../../common/constants/api-constants';

/**
 * WECOOL-52277 — types for the new aggregated HECLR endpoint that feeds the
 * My Digital Products page. Keep all sub-shapes in this single file so the
 * contract is easy to reconcile if the endpoint changes.
 */

export enum OptInStatus {
  OPEN = 'open',
  CLOSED = 'closed',
}

export type OptInStatusValue = `${OptInStatus}`;

export interface MyDigitalProductImage {
  /** Real QALV field: absolute cover image URL. */
  url?: string;
  altText: string;
  /** @deprecated legacy/idealized field retained for the local mock. Use `url`. */
  coverImage?: string;
}

export interface MyDigitalProductTitle {
  /** Real QALV field: product display title. */
  text?: string;
  edition?: string;
  authors?: string[];
  copyrightYear?: string;
  isEvergreen?: boolean;
  /** @deprecated legacy/idealized field retained for the local mock. Use `text`. */
  productDescription?: string;
}

export interface MyDigitalProductIdentity {
  isbn10: string;
  isbn13?: string;
  copyrightYear?: number;
  productSegment?: string;
  businessUnit?: string;
}

export interface MyDigitalProductCourseContext {
  /** Real QALV field. */
  schoolName?: string | null;
  courseName?: string | null;
  department?: string | null;
  instructorName?: string | null;
  courseNumber?: string | null;
  sectionXid?: string | null;
  orgXid?: string | null;
  /** @deprecated legacy/idealized field retained for the local mock. Use `schoolName`. */
  school?: string | null;
}

export interface MyDigitalProductExpiry {
  /** Omitted for standalone Inclusive Access records without a matching product license. */
  licenseEndDate?: string;
  isExpired: boolean;
  expiresOn: string;
}

export interface MyDigitalProductFreeTrialBlock {
  isFreeTrial: boolean;
  freeTrialInfo: unknown | null;
}

export interface MyDigitalProductCta {
  type: string;
  kind?: 'anchor' | 'button' | 'modal' | 'text' | 'none';
  label: string;
  url?: string;
  /** @deprecated retained for compatibility with the original frontend mock. */
  href?: string | null;
  target?: string;
  ariaLabel?: string;
  automationId?: string;
  icon?: string;
  accessCode?: string | null;
  ebookUrls?: string[];
  secondaryCta?: {
    kind: string;
    label: string;
    href: string;
  };
  modal?: {
    type: string;
    isbn: string;
    orderId: string;
  };
  /** @deprecated retained for compatibility with the original frontend mock. */
  displayCode?: boolean;
}

/**
 * Future POST payload (phase 2). Typed now, not used in phase 1.
 */
export interface UserSectionOptInOutMetaData {
  dealId: number;
  sectionXid: string;
  isbn: string;
  expiryDate: string | null;
  sectionId: number;
  sectionUuid: string;
  organizationXid: string;
  instructorXid: string;
  platform: string;
  /** Returned by HECLR for the opt-in confirmation modal (WECOOL-52663). */
  price?: string;
  currencyCode?: string;
  countryCode?: string;
}

export interface InclusiveAccessBlock {
  productIsbn: string;
  productDescription: string;
  /** Optional: not always present in the QALV response. */
  edition?: number;
  formatCode: string;
  formatDescription: string;
  authors: string[];
  /** ISO date of the most recent opt-in/out record update for this ISBN. */
  lastUpdatedDate: string;
  /** End of the change-preference window. Null = no end date ("Open"). */
  expiryDate: string | null;
  isbn10: string;
  isbn13: string;
  coverImage: string;
  virtual: boolean;
  mhid: string;
  productSegment: string;
  productBrandingCode: string;
  copyrightYear: number;
  isUserOptedIn: boolean;
  /** Server-driven. Trust this over date comparisons. */
  optInStatus: OptInStatusValue;
  /**
   * Optional. Not in the current endpoint sample but Figma shows an opt-in price.
   * Render conditionally; remove if final contract excludes it.
   */
  price?: number;
  userSectionOptInOutMetaData: UserSectionOptInOutMetaData;
}

export interface MyDigitalProductAggregatedItem {
  image?: MyDigitalProductImage;
  title?: MyDigitalProductTitle;
  identity: MyDigitalProductIdentity;
  courseContext?: MyDigitalProductCourseContext;
  typeLabel?: string;
  expiry?: MyDigitalProductExpiry;
  freeTrial?: MyDigitalProductFreeTrialBlock;
  /** Real QALV response returns an array; the local mock uses a single object. */
  cta?: MyDigitalProductCta | MyDigitalProductCta[];
  /** Null when the product has no Inclusive Access offering. */
  inclusiveAccess: InclusiveAccessBlock | null;
}

export interface MyDigitalProductsAggregatedState extends ApiState {
  data: MyDigitalProductAggregatedItem[] | undefined;
}
